import { CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import Drafts from './Drafts'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import { Feature, OnboardingStep, TwinResponderLabel, TwinResponderStatus } from 'api/model'
import { enhanceDraftOpenAtom } from 'state/enhance-draft'
import { useRecoilState, useRecoilValue } from 'recoil'
import { myTwinAtom } from 'state/my-twin'
import { useFetchTwinResponder, useFetchTwinResponderLabels } from 'hooks/use-email-responder'
import SetupPermissionsModal from 'components/Permissions'
import { authenticatedUserAtom } from 'state'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import GmailFtuxModal from 'components/FtuxModal/GmailFtuxModal'
import SettingsModal from './SettingsModal'
import { useSearchParams } from 'react-router-dom'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { COLORS } from 'app-constants'
import { Banner } from 'components/Slack'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import AcquisitionBanner from 'components/AcquisitionBanner/AcquisitionBanner'

const AdvancedSettingsBanner = ({
  acceptedPermissions,
  missingPermissions,
  updateOnboardingStep,
}: {
  acceptedPermissions: string[]
  missingPermissions: string[]
  updateOnboardingStep: () => void
}) => {
  const user = useRecoilValue(authenticatedUserAtom)

  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (
      !user?.onboarding_steps_completed.includes(OnboardingStep.advanced_email_responder_banner_shown) &&
      missingPermissions.length === 0
    ) {
      updateOnboardingStep()
    }
  }, [missingPermissions, user?.onboarding_steps_completed, updateOnboardingStep])

  const shouldShowAdvancedSettingsBanner =
    !user?.onboarding_steps_completed.includes(OnboardingStep.advanced_email_responder_banner_shown) &&
    missingPermissions.length > 0

  const onButtonClick = () => {
    setIsPermissionsModalOpen(true)
  }
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }

  if (!shouldShowAdvancedSettingsBanner) {
    return null
  }

  return (
    <Stack mb={1}>
      <Banner
        icon={
          <ForwardToInboxOutlinedIcon
            style={{
              width: '20px',
              height: '20px',
              color: COLORS.JELLED_PRIMARY_BLUE,
            }}
          />
        }
        title="Organize drafts and incoming emails"
        description="Grant additional permissions to automatically move drafts and emails to new folders."
        buttonText="Grant permissions"
        onClick={onButtonClick}
        onDismissClicked={() => {
          updateOnboardingStep()
          setIsPermissionsModalOpen(false)
        }}
      />
      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          additionalParams={{
            isSettingsModalOpen: 'true', // used to open the settings modal after permissions are granted
            isActivateEmailResponderActive: 'true', // used to turn the toggle on after permissions are granted
          }}
          onClose={onClosePermissionsModal}
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          feature={Feature.advanced_email_responder}
        />
      )}
    </Stack>
  )
}

export const AdvancedSettingsBannerWithOnboardingStep = withOnboardingStep(
  AdvancedSettingsBanner,
  OnboardingStep.advanced_email_responder_banner_shown
)

const Emails = ({
  isEnhancingDraft,
  selectedTwinResponderLabels,
  isLoadingLabels,
  twinResponderLabels,
}: {
  isEnhancingDraft: boolean
  selectedTwinResponderLabels: TwinResponderLabel[]
  isLoadingLabels: boolean
  twinResponderLabels: TwinResponderLabel[]
}) => {
  const isMobile = useCheckMobileScreen()

  const { featurePermissions, isLoading: isLoadingPermissions } = useFetchCheckPermissions(
    Feature.advanced_email_responder
  )
  const acceptedPermissions = featurePermissions?.acceptedPermissions ?? []
  const missingPermissions = useMemo(() => {
    return featurePermissions?.missingPermissions ?? []
  }, [featurePermissions])

  if (isLoadingPermissions) {
    return (
      <Stack height="100vh" width="100%" textAlign="center" justifyContent="center" alignItems="center">
        <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
      </Stack>
    )
  }

  return (
    <Stack width={isEnhancingDraft && !isMobile ? 'calc(100% - 384px)' : '100%'} p={isMobile ? 0 : 4}>
      <AdvancedSettingsBannerWithOnboardingStep
        acceptedPermissions={acceptedPermissions}
        missingPermissions={missingPermissions}
      />
      <Drafts
        isLoadingLabels={isLoadingLabels}
        twinResponderLabels={twinResponderLabels}
        selectedTwinResponderLabels={selectedTwinResponderLabels}
      />
    </Stack>
  )
}

const Gmail = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const user = useRecoilValue(authenticatedUserAtom)
  const myTwin = useRecoilValue(myTwinAtom)
  const isMobile = useCheckMobileScreen()

  const [tabIndex, setTabIndex] = useState<number>(0)
  // when tab index changes, we should update the current url
  useEffect(() => {
    if (tabIndex === 0) {
      window.history.replaceState({}, '', '/emails')
    } else {
      window.history.replaceState({}, '', '/emails?currentPage=0')
    }
  }, [tabIndex])

  const [searchParams] = useSearchParams()

  // when the component first loads, we want to see which tab we should be on
  useEffect(() => {
    if (!!searchParams.get('currentPage')) {
      setTabIndex(1)
    } else {
      setTabIndex(0)
    }
  }, [searchParams])

  const {
    twinResponder,
    isLoading: isLoadingTwinResponder,
    setTwinResponder,
    refetchTwinResponder,
  } = useFetchTwinResponder(myTwin?.id ?? '')

  useEffect(() => {
    // every hour, we should refetch the twin responder
    const interval = setInterval(() => {
      refetchTwinResponder()
    }, 1000 * 60 * 60)
    return () => clearInterval(interval)
  }, [refetchTwinResponder])

  const { featurePermissions, isLoading: isLoadingPermissions } = useFetchCheckPermissions(Feature.email_responder)
  const missingPermissions = useMemo(() => {
    return featurePermissions?.missingPermissions ?? []
  }, [featurePermissions])

  const [isEnhancingDraft] = useRecoilState(enhanceDraftOpenAtom)

  const hasGmailFtuxBeenShown = user?.onboarding_steps_completed.includes(OnboardingStep.email_responder_modal_shown)
  const hasCompletedEmailResponderSettings = user?.onboarding_steps_completed.includes(OnboardingStep.email_responder)
  const [isGmailFtuxModalOpen, setIsGmailFtuxModalOpen] = useState<boolean>(false)
  // show ftux modal if permissions are granted and ftux hasn't been shown and if twin responder status is inactive
  useEffect(() => {
    if (missingPermissions.length === 0 && !hasGmailFtuxBeenShown) {
      setIsGmailFtuxModalOpen(true)
    }
  }, [missingPermissions, hasGmailFtuxBeenShown])

  const [isGmailSettingsModalOpen, setIsGmailSettingsModalOpen] = useState<boolean>(false)

  useLayoutEffect(() => {
    // if we've just set it to be active, we should close the modal
    if (twinResponder?.status === TwinResponderStatus.active) {
      setIsGmailSettingsModalOpen(false)
      return
    }

    // if we have shown the gmail ftux and if there are no missing permissions, we should show the gmail settings modal
    if (
      !isGmailFtuxModalOpen &&
      !isLoadingPermissions &&
      !isLoadingLabels &&
      !isLoadingTwinResponder &&
      missingPermissions.length === 0 &&
      hasGmailFtuxBeenShown &&
      !hasCompletedEmailResponderSettings &&
      twinResponder &&
      twinResponder?.status === TwinResponderStatus.inactive
    ) {
      setIsGmailSettingsModalOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twinResponder])

  const {
    twinResponderLabels,
    selectedTwinResponderLabels,
    isLoading: isLoadingLabels,
  } = useFetchTwinResponderLabels(myTwin?.id ?? '')

  if (isLoadingPermissions || isLoadingTwinResponder || isLoadingLabels) {
    if (isMobile) {
      return (
        <Stack>
          <Stack height="100vh" width="100%" textAlign="center" justifyContent="center" alignItems="center">
            <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
          </Stack>
        </Stack>
      )
    }

    return (
      <Stack>
        <Stack
          width="100%"
          py="40px"
          pl="41px"
          direction="row"
          alignItems="center"
          spacing={5}
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
            borderBottom: `1px solid ${COLORS.GRAY_5}`,
          }}>
          <Typography variant="h4">Emails</Typography>
        </Stack>

        <Stack height="100vh" width="100%" textAlign="center" justifyContent="center" alignItems="center">
          <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack>
      {!isMobile && (
        <Stack
          width="100%"
          py="40px"
          pl="41px"
          direction="row"
          alignItems="center"
          spacing={5}
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
            borderBottom: `1px solid ${COLORS.GRAY_5}`,
          }}>
          <Typography variant="h4">Emails</Typography>
        </Stack>
      )}

      <AcquisitionBanner />

      <Emails
        isEnhancingDraft={isEnhancingDraft}
        twinResponderLabels={twinResponderLabels}
        selectedTwinResponderLabels={selectedTwinResponderLabels}
        isLoadingLabels={isLoadingLabels}
      />

      {isGmailFtuxModalOpen && (
        <GmailFtuxModal
          onClose={() => {
            setIsGmailFtuxModalOpen(false)
          }}
          onFinish={() => {
            setIsGmailFtuxModalOpen(false)
            updateOnboardingStep()
            setIsGmailSettingsModalOpen(true)
          }}
        />
      )}

      {isGmailSettingsModalOpen && (
        <SettingsModal
          isLoadingLabels={isLoadingLabels}
          onClose={() => {
            setIsGmailSettingsModalOpen(false)
          }}
          twinId={myTwin?.id ?? ''}
          twinResponder={twinResponder}
          setTwinResponder={setTwinResponder}
          twinResponderLabels={twinResponderLabels}
        />
      )}
    </Stack>
  )
}

export default withOnboardingStep(Gmail, OnboardingStep.email_responder_modal_shown)
